import CircularProgress from '@material-ui/core/CircularProgress';
import StarIcon from '@material-ui/icons/Star';
import { apiState } from '@orientaction/api-actions';
import { SimpleModalTest } from '@orientaction/components';
import { CompassUserContext, CompassUserProvider } from '@orientaction/Context';
import { useAlert, useCategoryTest, useLanguage, useMajorTest } from '@orientaction/hooks';
import { userService } from '@orientaction/services';
import {
  getFromLS,
  isAdmin,
  publicImgMajorTest,
  publicSvgPath,
  updateUserInfoInLS,
  userHasService,
} from '@orientaction/utils';
import { ChartBar } from '@phosphor-icons/react';
import clsx from 'clsx';
import { kebabCase } from 'lodash';
import { ChangeEvent, Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getMe, sendMailForMore } from '../../../../services/user.service';
import { apiServiceNative } from '../../../../utils/fetchNative';
import { getMajorTestEngUrlByTestId } from '../../../UserTest/TestMajor/utils';
import {
  EmptyState,
  MinorTestCard,
  PageHeader,
  RedesignLayout,
  SectionHeader,
  useSharedStyles,
} from '../../shared';
import RedesignImage from '../../shared/components/RedesignImage';
import { formatTestDuration } from '../../shared/utils/formatTestDuration';
import { redesignTheme } from '../../theme';
import TestLimitModal from '../components/TestLimitModal';
import { getMajorTestTemplateDescription } from '../shared/majorTestDescriptions';
import PendingTestsModal from './components/PendingTestsModal';
import { useMyTestsStyles } from './index.styles';

type MajorTestItem = {
  id: number | string;
  name: string;
  route: string;
  isActive?: boolean;
  srcImg?: string;
  time?: string;
  description?: string;
};

type MinorTestItem = {
  id: number | string;
  title: string;
  description?: string;
  image?: string;
  thumbnail?: string;
  time?: string;
  state?: 'started' | 'over' | string;
};

type MinorCategory = {
  id: number | string;
  name: string;
  subtitle?: string;
  icon?: string;
  contentList?: MinorTestItem[];
};

type MajorResultPath = {
  testId: number;
  path: string;
};

type MinorResultPath = {
  testId: number;
  path: string;
};

const MAJOR_RESULT_CONFIGS = [
  {
    key: 'meaningTestLastResult',
    testId: 1,
    path: (id: number | string) => `/resultat-meaning-value/${id}`,
  },
  { key: 'needsTestLastResult', testId: 2, path: (id: number | string) => `/resultat-need/${id}` },
  {
    key: 'needsProTestLastResult',
    testId: 3,
    path: (id: number | string) => `/resultat-need-pro/${id}`,
  },
  {
    key: 'preferenceTestLastResult',
    testId: 4,
    path: (id: number | string) => `/resultat-preference/${id}`,
  },
  {
    key: 'harmonyTestLastResult',
    testId: 5,
    path: (id: number | string) => `/resultat-harmony/${id}`,
  },
  { key: 'studyTestLastResult', testId: 6, path: (id: number | string) => `/resultat-study/${id}` },
  {
    key: 'preferenceJeuneLastResult',
    testId: 7,
    path: (id: number | string) => `/res-pref-j/${id}`,
  },
];

const getBuyLinkByTest = (id: number | string) => {
  switch (+id) {
    case 1:
      return 'https://orientaction.kneo.me/shop/view/D0A37F';
    case 2:
      return 'https://orientaction.kneo.me/shop/view/120C39';
    case 3:
      return 'https://orientaction.kneo.me/shop/view/5726D7';
    case 4:
      return 'https://orientaction.kneo.me/shop/view/752F26';
    case 5:
      return 'https://orientaction.kneo.me/shop/view/1755C3';
    case 6:
      return 'https://orientaction.kneo.me/shop/view/7EC7E8';
    case 7:
      return 'https://orientaction.kneo.me/shop/view/K0976011';
    default:
      return '';
  }
};

const parseStoredUser = () => {
  try {
    return JSON.parse(getFromLS('userInfos') || '{}');
  } catch (error) {
    return {};
  }
};

const normalize = (value?: string | null) => (value ?? '').toLowerCase().trim();

const FRENCH_CARDINALS: Record<number, string> = {
  1: 'un',
  2: 'deux',
  3: 'trois',
  4: 'quatre',
  5: 'cinq',
  6: 'six',
  7: 'sept',
  8: 'huit',
  9: 'neuf',
  10: 'dix',
};

const getMajorTestsTitle = (count: number) => {
  const word = FRENCH_CARDINALS[count] ?? String(count);

  if (count <= 1) {
    return 'Votre test majeur';
  }

  return `Vos ${word} tests majeurs`;
};

const buildMajorResultPaths = (latestResults: any): MajorResultPath[] =>
  MAJOR_RESULT_CONFIGS.reduce<MajorResultPath[]>((acc, config) => {
    const result = latestResults?.[config.key];

    if (result?.id) {
      acc.push({
        testId: config.testId,
        path: config.path(result.id),
      });
    }

    return acc;
  }, []);

const buildMinorResultPaths = (latestResults: any): MinorResultPath[] =>
  (latestResults?.customTestResults || [])
    .filter((result: any) => result?.id && result?.test?.id)
    .map((result: any) => ({
      testId: +result.test.id,
      path: `/mes-resultats/${result.id}`,
    }));

const MyTests = () => {
  const classes = useMyTestsStyles();
  const sharedClasses = useSharedStyles();
  const history = useHistory();
  const alert = useAlert();
  const storedUser = useMemo(parseStoredUser, []);
  const token = getFromLS('userToken') || '';
  const { getFilterTest } = useCategoryTest();
  const { getMyMajorTest, getUserAllTestLatestResult } = useMajorTest();
  const { language } = useLanguage();
  const { tabLayout } = useSelector(apiState);
  const { userInfos } = useContext<any>(CompassUserContext);

  const [searchValue, setSearchValue] = useState('');
  const [me, setMe] = useState<any>(storedUser);
  const [testsToDo, setTestsToDo] = useState<any[]>([]);
  const [freeTests, setFreeTests] = useState<Array<number | string>>([]);
  const [majorTests, setMajorTests] = useState<MajorTestItem[]>([]);
  const [minorCategories, setMinorCategories] = useState<MinorCategory[]>([]);
  const [majorResultPaths, setMajorResultPaths] = useState<MajorResultPath[]>([]);
  const [minorResultPaths, setMinorResultPaths] = useState<MinorResultPath[]>([]);
  const [majorLoading, setMajorLoading] = useState(false);
  const [minorLoading, setMinorLoading] = useState(false);
  const [openPendingTestsModal, setOpenPendingTestsModal] = useState(false);
  const [openUnlockModal, setOpenUnlockModal] = useState(false);
  const [openingTestId, setOpeningTestId] = useState<number | string | null>(null);

  const effectiveUser = userInfos || me || storedUser;
  const userInCardOrNonRattached = !effectiveUser?.rattachement || userHasService(effectiveUser, 5);
  const normalizedSearch = normalize(searchValue);
  const majorResultByTestId = useMemo(
    () =>
      majorResultPaths.reduce<Record<number, string>>((acc, result) => {
        acc[result.testId] = result.path;
        return acc;
      }, {}),
    [majorResultPaths]
  );
  const minorResultByTestId = useMemo(
    () =>
      minorResultPaths.reduce<Record<number, string>>((acc, result) => {
        acc[result.testId] = result.path;
        return acc;
      }, {}),
    [minorResultPaths]
  );

  useEffect(() => {
    let isMounted = true;

    const fetchMe = async () => {
      try {
        const dataMe = await getMe(token);

        if (isMounted && dataMe?.data) {
          setMe(dataMe.data);
          setTestsToDo(dataMe.data.tests_to_do || []);
        }
      } catch (error) {
        alert.openSimpleAlert();
      }
    };

    const fetchFreeTests = async () => {
      try {
        const response = await apiServiceNative.get('/api/free-test?populate=tests');
        const testIds = response.data.attributes.tests.data.map((test: any) => test.id);

        if (isMounted) {
          setFreeTests(testIds);
        }
      } catch (error) {
        alert.openSimpleAlert();
      }
    };

    const fetchMajorTests = async () => {
      setMajorLoading(true);

      try {
        const [tests, latestResults] = await Promise.all([
          getMyMajorTest(''),
          getUserAllTestLatestResult(),
        ]);

        if (isMounted) {
          setMajorTests(tests || []);
          setMajorResultPaths(buildMajorResultPaths(latestResults));
          setMinorResultPaths(buildMinorResultPaths(latestResults));
        }
      } catch (error) {
        alert.openSimpleAlert();
      } finally {
        if (isMounted) {
          setMajorLoading(false);
        }
      }
    };

    const fetchMinorTests = async () => {
      setMinorLoading(true);

      try {
        const response = await getFilterTest('');
        const shouldHideMinorTests = userHasService(storedUser, 6);

        if (isMounted) {
          setMinorCategories(shouldHideMinorTests ? [] : response || []);
        }
      } catch (error) {
        alert.openSimpleAlert();
      } finally {
        if (isMounted) {
          setMinorLoading(false);
        }
      }
    };

    if (token) {
      fetchMe();
      fetchFreeTests();
    }

    fetchMajorTests();
    fetchMinorTests();

    return () => {
      isMounted = false;
    };
    // Project hooks return fresh functions; keep the legacy one-time fetch behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const updateFirstConnexion = async () => {
      updateUserInfoInLS({ isMyFirstTest: true });
      await userService.modifyUser({
        token,
        isMyFirstTest: true,
        id: storedUser?.id,
      });
    };

    const checkFirstConnexion = async () => {
      const dataMe = await getMe(token);

      if (
        !dataMe.data.isMyFirstTest &&
        dataMe.data.beneficiaryType === 'rattached' &&
        !isAdmin(dataMe.data)
      ) {
        await updateFirstConnexion();
      }
    };

    if (tabLayout === 1 && token) {
      checkFirstConnexion();
    }
  }, [storedUser?.id, tabLayout, token]);

  const filteredMajorTests = useMemo(() => {
    const sortedTests = [...majorTests].sort((first, second) => +first.id - +second.id);

    if (!normalizedSearch) {
      return sortedTests;
    }

    return sortedTests.filter(test => normalize(test.name).includes(normalizedSearch));
  }, [majorTests, normalizedSearch]);

  const filteredMinorCategories = useMemo(() => {
    const categories = minorCategories.map(category => ({
      ...category,
      contentList: (category.contentList || []).filter(test => {
        if (!normalizedSearch) {
          return true;
        }

        return (
          normalize(test.title).includes(normalizedSearch) ||
          normalize(test.description).includes(normalizedSearch) ||
          normalize(category.name).includes(normalizedSearch) ||
          normalize(category.subtitle).includes(normalizedSearch)
        );
      }),
    }));

    return categories.filter(category => (category.contentList || []).length > 0);
  }, [minorCategories, normalizedSearch]);

  const isLoading = majorLoading || minorLoading;
  const hasNoResults =
    !isLoading && filteredMajorTests.length === 0 && filteredMinorCategories.length === 0;

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const getMajorRoute = (test: MajorTestItem) => {
    if (!userInCardOrNonRattached && language === 'GB') {
      return getMajorTestEngUrlByTestId(test.id) || test.route;
    }

    return test.route;
  };

  const handleMajorAction = (test: MajorTestItem) => {
    const resultPath = majorResultByTestId[+test.id];

    if (resultPath) {
      history.push(resultPath);
      return;
    }

    if (!test.isActive && userInCardOrNonRattached) {
      const buyLink = getBuyLinkByTest(test.id);

      if (buyLink) {
        window.open(buyLink, '_blank', 'noopener,noreferrer');
      }

      return;
    }

    const route = getMajorRoute(test);

    if (route) {
      window.open(route, '_blank', 'noopener,noreferrer');
    }
  };

  const handleMinorAction = async (test: MinorTestItem) => {
    if (test.state === 'over') {
      history.push(minorResultByTestId[+test.id] || '/mes-resultats');
      return;
    }

    setOpeningTestId(test.id);

    try {
      const dataMe = await getMe(token);
      const freshUser = dataMe.data;
      const minimum = freshUser.beneficiaryType === 'rattached' ? 10 : 3;
      const testsToDoIds = freshUser.tests_to_do?.map((item: any) => item.id) || [];

      if (
        freeTests.includes(test.id) ||
        freshUser.counterTest === null ||
        freshUser.counterTest < minimum ||
        freshUser.statusSubscription === 'active' ||
        testsToDoIds.includes(test.id)
      ) {
        history.push(`/test/${test.id}/${kebabCase(test.title)}`);
      } else {
        setMe(freshUser);
        setOpenUnlockModal(true);
      }
    } catch (error) {
      alert.openSimpleAlert();
    } finally {
      setOpeningTestId(null);
    }
  };

  const sendMail = async () => {
    if (!me?.id) {
      setOpenUnlockModal(false);
      return;
    }

    const response = await sendMailForMore(me.id, token);

    if (response) {
      setOpenUnlockModal(false);
    }
  };

  const paySubscription = () => {
    if (!me?.id || !me?.email) {
      alert.openSimpleAlert();
      return;
    }

    const paymentLink =
      me.beneficiaryType === 'rattached'
        ? process.env.REACT_APP_PAYMENT_LINK
        : process.env.REACT_APP_PAYMENT_LINK_NO_RATTACHED;

    window.open(
      `${paymentLink}?prefilled_email=${me.email}&locale=${
        language === 'GB' ? 'eng' : 'fr'
      }&client_reference_id=${me.id}`,
      '_blank'
    );
    setOpenUnlockModal(false);
  };

  const goToAdvisedTest = (test: any) => {
    history.push(`/test/${test?.id}/${kebabCase(test?.title || '')}`);
  };

  const renderMajorCard = (test: MajorTestItem) => {
    const resultPath = majorResultByTestId[+test.id];
    const isLocked = !test.isActive && userInCardOrNonRattached;
    const image = test.srcImg ? `${publicImgMajorTest}/${test.srcImg}` : `${publicSvgPath}/QR.png`;

    const actionLabel = resultPath
      ? 'Voir mes résultats'
      : isLocked
      ? 'Acheter ce test'
      : 'Faire le test';

    return (
      <div key={test.id} className={classes.cardGridItem}>
        <MinorTestCard
          title={test.name}
          image={image}
          time={test.time}
          description={getMajorTestTemplateDescription(+test.id, test.description)}
          status={resultPath ? 'done' : 'todo'}
          buttonLabel={actionLabel}
          showResultIcon={Boolean(resultPath)}
          locked={isLocked}
          durationFallback="20"
          onAction={() => handleMajorAction(test)}
        />
      </div>
    );
  };

  const renderMinorCard = (test: MinorTestItem) => {
    const isInProgress = test.state === 'started';
    const isDone = test.state === 'over';
    const isOpening = openingTestId === test.id;
    const buttonLabel = isInProgress
      ? 'Continuer le test'
      : isDone
      ? 'Voir mes résultats'
      : 'Faire le test';

    return (
      <div key={test.id} className={classes.cardGridItem}>
        <MinorTestCard
          title={test.title}
          image={test.image}
          thumbnail={test.thumbnail}
          time={test.time}
          buttonLabel={buttonLabel}
          showResultIcon={isDone}
          loading={isOpening}
          onAction={() => handleMinorAction(test)}
        />
      </div>
    );
  };

  const renderMinorCategory = (category: MinorCategory) => {
    const tests = category.contentList || [];

    return (
      <section key={category.id} className={classes.categoryPanel}>
        <div className={classes.categoryHeader}>
          <div className={classes.categoryTitleWrap}>
            <span className={classes.categoryIcon}>
              {category.icon ? (
                <RedesignImage
                  className={classes.categoryIconImage}
                  src={category.icon}
                  alt=""
                  aria-hidden={true}
                />
              ) : (
                <StarIcon className={classes.cardButtonIcon} />
              )}
            </span>
            <div className={classes.categoryHeaderMain}>
              <div className={classes.categoryHeaderContent}>
                <p className={classes.categoryEyebrow}>{category.name}</p>
                {category.subtitle && (
                  <h3 className={classes.categoryTitle}>{category.subtitle}</h3>
                )}
              </div>
              <span className={classes.countPill}>
                {tests.length} {tests.length > 1 ? 'tests' : 'test'}
              </span>
            </div>
          </div>
        </div>
        <div className={classes.categoryBody}>
          <div className={classes.cardGrid}>{tests.map(renderMinorCard)}</div>
        </div>
      </section>
    );
  };

  const renderEmptyState = () => (
    <EmptyState
      title="Aucun résultat trouvé"
      description={
        <>
          Aucun test ne correspond à votre recherche{' '}
          <strong>&quot;{searchValue.trim()}&quot;</strong>. Essayez avec d&apos;autres mots-clés.
        </>
      }
    />
  );

  return (
    <CompassUserProvider>
      <RedesignLayout isaddBackground={false} footer={true} noPadding={true}>
        <main className={clsx(sharedClasses.pageShell, classes.page)}>
          <PendingTestsModal
            open={openPendingTestsModal}
            tests={testsToDo}
            showBanner={true}
            onOpen={() => setOpenPendingTestsModal(true)}
            onClose={() => setOpenPendingTestsModal(false)}
            onTestClick={test => {
              setOpenPendingTestsModal(false);
              goToAdvisedTest(test);
            }}
          />

          <PageHeader
            breadcrumbs={[{ label: 'Accueil', href: '/main' }, { label: 'Faire un test' }]}
            title="Faire un test"
            description="Retrouvez ici nos tests et questionnaires, tous proposés sur la base du volontariat. Si vous bénéficiez d’un accompagnement ORIENTACTION, vous y trouverez ceux prévus dans votre prestation ; les autres s’utilisent à titre personnel, indépendamment de toute prestation. Les résultats sont des repères, non un diagnostic. Consultez nos CGU et notre politique de confidentialité."
            titleVariant="primary"
            descriptionVariant="brown"
            search={{
              value: searchValue,
              onChange: handleSearchChange,
              ariaLabel: 'Rechercher un test',
            }}
          />

          {isLoading ? (
            <div className={sharedClasses.loadingCenter}>
              <CircularProgress style={{ color: redesignTheme.colors.primaryDark }} />
            </div>
          ) : hasNoResults ? (
            renderEmptyState()
          ) : (
            <div className={classes.sections}>
              {filteredMajorTests.length > 0 && (
                <section className={classes.section}>
                  <SectionHeader
                    tag="Tests standardisés"
                    title={getMajorTestsTitle(filteredMajorTests.length)}
                    description="Les fondations de votre profil. Conçus par nos psychologues, validés scientifiquement, ils dessinent votre cartographie complète."
                  />
                  <div className={classes.cardGrid}>{filteredMajorTests.map(renderMajorCard)}</div>
                </section>
              )}

              {filteredMinorCategories.length > 0 && (
                <section className={classes.section}>
                  <SectionHeader
                    tag="Tests non standardisés"
                    title="Aller plus loin, sur mesure"
                    description="Une sélection de tests thématiques pour creuser un sujet précis : reconnaissance, intelligence émotionnelle, leadership, communication..."
                  />
                  {filteredMinorCategories.map(renderMinorCategory)}
                </section>
              )}
            </div>
          )}
        </main>

        <PendingTestsModal
          open={openPendingTestsModal}
          tests={testsToDo}
          showBanner={true}
          onOpen={() => setOpenPendingTestsModal(true)}
          onClose={() => setOpenPendingTestsModal(false)}
          onTestClick={test => {
            setOpenPendingTestsModal(false);
            goToAdvisedTest(test);
          }}
        />

        <TestLimitModal open={openUnlockModal} onClose={sendMail} onUnlock={paySubscription} />

      </RedesignLayout>
    </CompassUserProvider>
  );
};

export default MyTests;
